// app/javascript/controllers/page_transition_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    duration: { type: Number, default: 800 },
    type: { type: String, default: "slide" } // Valeurs possibles: "slide" (défaut), "fade"
  }
  
  static targets = ["container"]

  connect() {
    console.log(`🔄 Contrôleur de transition connecté (type: ${this.typeValue})`)
    
    // Nettoyer les états précédents au cas où
    this.cleanupTransitionStates()
    
    // Si utilisé avec Turbo, écouter les événements de navigation
    this.bindTurboEvents()
    
    // Gestion du retour arrière du navigateur
    this.handleBrowserNavigation()
    
    // Animation initiale d'entrée (seulement si on n'est pas en retour arrière)
    if (!this.isPageFromCache()) {
      this.startInTransition()
    }
  }
  
  disconnect() {
    this.unbindTurboEvents()
    this.cleanupTransitionStates()
    window.removeEventListener('pageshow', this.handlePageShow.bind(this))
  }

  bindTurboEvents() {
    // Utiliser addEventListener avec une référence à la fonction pour pouvoir la supprimer
    this.turboBeforeVisitHandler = this.prepareTransition.bind(this)
    this.turboVisitHandler = this.startOutTransition.bind(this)
    this.turboLoadHandler = this.handleTurboLoad.bind(this)
    
    document.addEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
    document.addEventListener("turbo:visit", this.turboVisitHandler)
    document.addEventListener("turbo:load", this.turboLoadHandler)
  }

  unbindTurboEvents() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
    }
    if (this.turboVisitHandler) {
      document.removeEventListener("turbo:visit", this.turboVisitHandler)
    }
    if (this.turboLoadHandler) {
      document.removeEventListener("turbo:load", this.turboLoadHandler)
    }
  }

  handleBrowserNavigation() {
    window.addEventListener('pageshow', this.handlePageShow.bind(this))
  }

  handlePageShow(event) {
    // Si la page est chargée depuis le cache (retour arrière)
    if (event.persisted) {
      console.log("Page loaded from cache, cleaning up transition states")
      this.cleanupTransitionStates()
    }
  }

  isPageFromCache() {
    // Vérifier si on vient d'un retour arrière
    return window.performance.navigation.type === 2
  }

  cleanupTransitionStates() {
    const element = this.hasContainerTarget ? this.containerTarget : document.body
    
    // Nettoyer toutes les classes de transition
    element.classList.remove(
      'animate-fade-out', 
      'animate-fade-in', 
      'page-exit', 
      'page-enter'
    )
    
    // S'assurer que l'élément est visible
    element.style.opacity = ''
    element.style.visibility = ''
    element.style.transform = ''
  }
  
  prepareTransition(event) {
    // Stocke l'URL pour la transition
    this.nextUrl = event.detail.url
    console.log(`Preparing transition to: ${this.nextUrl}`)
  }
  
  startOutTransition() {
    const element = this.hasContainerTarget ? this.containerTarget : document.body
    
    // Nettoyer d'abord les classes précédentes
    this.cleanupTransitionStates()
    
    console.log(`Starting out transition (${this.typeValue})`)
    
    if (this.typeValue === "fade") {
      element.classList.add('animate-fade-out')
    } else {
      element.classList.add('page-exit')
    }
  }

  handleTurboLoad(event) {
    // Quand la nouvelle page est chargée
    console.log("Turbo load complete, starting in transition")
    this.startInTransition()
  }
  
  startInTransition() {
    const element = this.hasContainerTarget ? this.containerTarget : document.body
    
    // Nettoyer d'abord les classes précédentes
    this.cleanupTransitionStates()
    
    console.log(`Starting in transition (${this.typeValue})`)
    
    if (this.typeValue === "fade") {
      element.classList.add('animate-fade-in')
    } else {
      element.classList.add('page-enter')
    }
    
    // Nettoyer après l'animation
    setTimeout(() => {
      if (this.typeValue === "fade") {
        element.classList.remove('animate-fade-in')
      } else {
        element.classList.remove('page-enter')
      }
      console.log("In transition completed")
    }, this.durationValue)
  }
  
  // Pour les transitions manuelles (sans Turbo)
  transitionTo(event) {
    event.preventDefault()
    const targetUrl = event.currentTarget.getAttribute('href')
    const transitionType = event.currentTarget.getAttribute('data-transition-type') || this.typeValue
    
    if (!targetUrl) {
      console.error("No target URL found for transition")
      return
    }
    
    const element = this.hasContainerTarget ? this.containerTarget : document.body
    
    // Nettoyer d'abord
    this.cleanupTransitionStates()
    
    console.log(`Manual transition to: ${targetUrl}`)
    
    if (transitionType === "fade") {
      element.classList.add('animate-fade-out')
    } else {
      element.classList.add('page-exit')
    }
    
    setTimeout(() => {
      window.location.href = targetUrl
    }, this.durationValue)
  }

  // Méthode utilitaire pour désactiver temporairement les transitions
  disableTransitions() {
    this.transitionsDisabled = true
    this.cleanupTransitionStates()
    console.log("Page transitions disabled")
  }

  enableTransitions() {
    this.transitionsDisabled = false
    console.log("Page transitions enabled")
  }
}
